import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Sparkles, Loader2, Plus, Trash2, X, UserPlus, Users, Download, Paperclip, BadgeCheck, FileCheck2,
  FileText, Layers, KanbanSquare, CalendarRange, Flag, DollarSign, BarChart3, Image as ImageIcon,
  FolderOpen, MessageSquare, Wand2, Send, Check, Palette, RefreshCw, CalendarClock, GitCompare, CheckCircle2, Building2,
  Maximize2, ChevronLeft, ChevronRight, ChevronDown,
  Link2, Copy, ExternalLink, ReceiptText,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid } from "recharts";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ConnectedAreas from "../../components/os/ConnectedAreas";
import TimbrarCFDIModal from "../../components/os/TimbrarCFDIModal";
import QuoteBuilder from "../../components/os/QuoteBuilder";
import QuoteInvoiceModal from "../../components/os/QuoteInvoiceModal";
import QuoteComments from "../../components/os/QuoteComments";
import QuoteHeaderFields from "../../components/os/QuoteHeaderFields";
import { api, API, formatApiError, mediaUrl, uploadImage } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const money = (n, c = "") => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${c ? " " + c : ""}`;
const fmtDate = (iso) => { const s = (iso || "").slice(0, 10); if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return ""; const [y, m, d] = s.split("-"); return `${d}/${m}/${y}`; };
const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm";
const COLS = [["backlog", "Backlog"], ["todo", "Por hacer"], ["in_progress", "En curso"], ["review", "Revisión"], ["done", "Hecho"]];

const CAMPAIGN_STATUSES = [
  { value: "active", label: "Activa", dot: "bg-emerald-500" },
  { value: "paused", label: "Pausada", dot: "bg-amber-500" },
  { value: "completed", label: "Completada", dot: "bg-violet" },
];

function CampaignStatusSelect({ studio, id, value, onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const cur = CAMPAIGN_STATUSES.find((s) => s.value === value) || CAMPAIGN_STATUSES[0];
  const change = async (v) => {
    setOpen(false);
    if (v === value) return;
    setSaving(true);
    try { await api.patch(`/studio/${studio}/${id}/status`, { status: v }); toast.success("Estado actualizado"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo actualizar el estado"); }
    finally { setSaving(false); }
  };
  return (
    <div className="relative">
      <button data-testid="campaign-status-btn" onClick={() => setOpen((o) => !o)} disabled={saving}
        className="inline-flex items-center gap-2 text-sm rounded-full border border-navy/15 px-4 py-2.5 hover:border-violet transition-colors disabled:opacity-50">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className={`w-2.5 h-2.5 rounded-full ${cur.dot}`} />}
        {cur.label}
        <ChevronDown className="w-4 h-4 text-navy/40" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-50 bg-white border border-navy/10 rounded-lg shadow-lg py-1 min-w-[170px]" data-testid="campaign-status-menu">
            {CAMPAIGN_STATUSES.map((s) => (
              <button key={s.value} data-testid={`campaign-status-${s.value}`} onClick={() => change(s.value)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-navy/5 text-left ${s.value === value ? "text-violet" : "text-navy/70"}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} /> {s.label}
                {s.value === value && <Check className="w-3.5 h-3.5 ml-auto" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const shareStatusMeta = {
  pending: { label: "Pendiente de aprobación", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Aprobado por el cliente", cls: "bg-green-100 text-green-700" },
  rejected: { label: "El cliente pidió cambios", cls: "bg-red-100 text-red-700" },
};

function ShareButton({ studio, id, kind, refId, className }) {
  const [open, setOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const link = share ? `${window.location.origin}/share/${share.token}` : "";
  const openShare = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/studio/${studio}/${id}/share`, { kind, ref_id: refId });
      setShare(data); setOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear el enlace"); }
    finally { setLoading(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success("Enlace copiado"); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("No se pudo copiar"); }
  };
  const revoke = async () => {
    if (!window.confirm("¿Revocar el enlace? El cliente ya no podrá abrirlo.")) return;
    try { await api.post(`/studio/shares/${share.token}/revoke`); toast.success("Enlace revocado"); setOpen(false); setShare(null); }
    catch { toast.error("No se pudo revocar"); }
  };
  const meta = share ? (shareStatusMeta[share.status] || shareStatusMeta.pending) : null;
  return (
    <>
      <button data-testid={`share-open-${kind}-${refId}`} onClick={openShare} disabled={loading}
        className={className || "inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-violet hover:text-violet transition-colors disabled:opacity-50"}>
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Compartir con cliente
      </button>
      {open && share && (
        <div className="fixed inset-0 z-[80] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="share-modal">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-navy/10">
              <Link2 className="w-4 h-4 text-violet shrink-0" />
              <span className="text-sm font-medium flex-1">Enlace para el cliente</span>
              <button onClick={() => setOpen(false)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-navy/50">Cualquier persona con este enlace podrá ver {kind === "moodboard" ? "el moodboard" : "el concept board"} en modo <b>solo lectura</b> y aprobarlo o pedir cambios.</p>
              <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${meta.cls}`} data-testid="share-status">
                {meta.label}{share.status !== "pending" && share.reviewer_name ? ` · ${share.reviewer_name}` : ""}
              </span>
              {share.status !== "pending" && share.reviewer_comment && (
                <p className="text-xs text-navy/60 italic border-l-2 border-navy/10 pl-2">"{share.reviewer_comment}"</p>
              )}
              <div className="flex items-center gap-2">
                <input readOnly value={link} data-testid="share-link" className={`${input} font-mono text-xs`} onFocus={(e) => e.target.select()} />
                <button onClick={copy} data-testid="share-copy" className="shrink-0 rounded-lg bg-navy text-white px-3 py-2.5 text-sm hover:opacity-90 inline-flex items-center gap-1.5">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex items-center justify-between pt-1">
                <a href={link} target="_blank" rel="noreferrer" data-testid="share-open-link" className="text-xs text-violet hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Abrir vista del cliente</a>
                <button onClick={revoke} data-testid="share-revoke" className="text-xs text-navy/40 hover:text-destructive inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Revocar enlace</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const PRIO = { low: "bg-navy/10 text-navy/60", medium: "bg-gold/15 text-gold", high: "bg-rose-500/10 text-rose-600" };
const STUDIO_LABEL = { gout: "GOÛT", tagma: "TAGMA", ops: "Operaciones", vetta: "VETTA", aura: "AURA" };

const TABS = [
  ["resumen", "Resumen", Layers], ["brief", "Brief", FileText], ["fases", "Fases", Flag],
  ["tablero", "Tablero", KanbanSquare], ["cronograma", "Cronograma", CalendarRange], ["sprints", "Sprints", CalendarRange],
  ["cotizacion", "Cotización", DollarSign], ["moodboards", "Moodboards", ImageIcon], ["conceptboard", "Concept Board", Palette], ["renders", "Renders IA", Wand2],
  ["finanzas", "Finanzas", DollarSign], ["metricas", "Métricas & ROI", BarChart3], ["archivos", "Archivos", FolderOpen],
  ["comentarios", "Comentarios", MessageSquare],
];

async function downloadBlob(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = href; a.download = filename || "archivo"; a.click();
    URL.revokeObjectURL(href);
  } catch (e) { toast.error("No se pudo descargar"); }
}

export default function StudioProject() {
  const { id } = useParams();
  const location = useLocation();
  const studio = location.pathname.startsWith("/os/tagma") ? "tagma" : "gout";
  const label = studio === "tagma" ? "Tagma Studio" : "GOÛT Studio";
  const backUrl = studio === "tagma" ? "/os/tagma" : "/os/gout";

  const [data, setData] = useState(null);
  const [tab, setTab] = useState("resumen");
  const [invite, setInvite] = useState(false);

  const load = useCallback(() => {
    api.get(`/studio/${studio}/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Error"));
  }, [studio, id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <OSLayout title={label}><div className="py-20 text-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></OSLayout>;
  const p = data.project;

  return (
    <OSLayout title={`${label} · ${p.name}`}>
      <div className="space-y-6">
        <Link to={backUrl} className="inline-flex items-center gap-1.5 text-sm text-navy/50 hover:text-navy transition-colors"><ArrowLeft className="w-4 h-4" /> {label}</Link>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-medium tracking-tight" data-testid="studio-title">{p.name}</h1>
            <div className="text-navy/50 mt-1">{p.client || "—"}{p.sector ? ` · ${p.sector}` : ""}</div>
          </div>
          <div className="flex items-center gap-3">
            {studio === "tagma" && <CampaignStatusSelect studio={studio} id={id} value={p.status} onChanged={load} />}
            <div className="flex -space-x-2">
              {(data.members || []).slice(0, 5).map((m) => (
                <div key={m.id} title={m.name} className="w-8 h-8 rounded-full bg-violet/15 text-violet text-xs flex items-center justify-center border-2 border-cream font-medium">
                  {(m.name || "?").slice(0, 1).toUpperCase()}
                </div>
              ))}
            </div>
            <button data-testid="studio-invite" onClick={() => setInvite(true)} className="inline-flex items-center gap-2 text-sm rounded-full border border-navy/15 px-4 py-2.5 hover:border-violet hover:text-violet transition-colors"><UserPlus className="w-4 h-4" /> Invitar</button>
          </div>
        </div>

        <ConnectedAreas module={studio} entityId={id} />

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-navy/10 pb-px">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} data-testid={`studio-tab-${k}`} onClick={() => setTab(k)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-violet text-violet" : "border-transparent text-navy/50 hover:text-navy"}`}>
              <Icon className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>

        <div data-testid={`studio-panel-${tab}`}>
          {tab === "resumen" && <Resumen p={p} data={data} />}
          {tab === "brief" && <Brief studio={studio} id={id} p={p} onSaved={load} />}
          {tab === "fases" && <Phases studio={studio} id={id} p={p} onChange={load} />}
          {tab === "tablero" && <Board studio={studio} id={id} data={data} onChange={load} />}
          {tab === "cronograma" && <Timeline data={data} />}
          {tab === "sprints" && <Sprints studio={studio} id={id} data={data} onChange={load} />}
          {tab === "cotizacion" && <Quote studio={studio} id={id} p={p} data={data} onSaved={load} />}
          {tab === "moodboards" && <Moodboards studio={studio} id={id} data={data} onChange={load} />}
          {tab === "conceptboard" && <ConceptBoard studio={studio} id={id} projectName={p.name} />}
          {tab === "renders" && <Renders studio={studio} id={id} p={p} onChange={load} />}
          {tab === "finanzas" && <Finance studio={studio} id={id} data={data} onChange={load} />}
          {tab === "metricas" && <Metrics studio={studio} id={id} p={p} data={data} onChange={load} />}
          {tab === "archivos" && <Files studio={studio} id={id} />}
          {tab === "comentarios" && <Comments studio={studio} id={id} />}
        </div>
      </div>

      {invite && <InviteDialog studio={studio} id={id} members={data.members} onClose={() => setInvite(false)} onChange={load} />}
    </OSLayout>
  );
}

/* ---------------- Resumen ---------------- */
function Resumen({ p, data }) {
  const b = p.brief || {};
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white border border-navy/10 rounded-lg p-6 space-y-4">
        <div className="overline text-navy/40">Brief</div>
        {b.objectives ? (
          <div className="space-y-3 text-sm">
            {[["Objetivos", b.objectives], ["Estilo", b.style], ["Tiempos", b.timeline], ["Entregables", b.deliverables], ["Notas", b.notes]].filter(([, v]) => v).map(([k, v]) => (
              <div key={k}><div className="text-navy/40 text-xs uppercase tracking-wide">{k}</div><div className="text-navy/80">{v}</div></div>
            ))}
          </div>
        ) : <div className="text-navy/40 text-sm">Aún no hay brief. Ve a la pestaña Brief para crearlo.</div>}
      </div>
      <div className="space-y-4">
        <div className="bg-white border border-navy/10 rounded-lg p-6">
          <div className="overline text-navy/40 mb-2">Progreso</div>
          <div className="text-3xl font-medium">{p.progress || 0}%</div>
          <div className="mt-3 h-2 bg-navy/10 rounded-full overflow-hidden"><div className="h-full bg-gradient-violet" style={{ width: `${p.progress || 0}%` }} /></div>
        </div>
        <div className="bg-white border border-navy/10 rounded-lg p-6 grid grid-cols-2 gap-3 text-center">
          <div><div className="text-2xl font-medium">{data.tasks.length}</div><div className="text-xs text-navy/40">Tareas</div></div>
          <div><div className="text-2xl font-medium" data-testid="overview-value">{money(data.finance.value ?? data.finance.income)}</div><div className="text-xs text-navy/40">Valor del proyecto</div></div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Brief ---------------- */
function Brief({ studio, id, p, onSaved }) {
  const [f, setF] = useState(p.brief || { objectives: "", style: "", timeline: "", deliverables: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [gen, setGen] = useState(false);
  const fields = [["objectives", "Objetivos"], ["style", "Estilo / dirección creativa"], ["timeline", "Tiempos"], ["deliverables", "Entregables"], ["notes", "Notas"]];
  const save = async () => { setBusy(true); try { await api.put(`/studio/${studio}/${id}/brief`, f); toast.success("Brief guardado"); onSaved(); } catch (e) { toast.error("Error"); } finally { setBusy(false); } };
  const generate = async () => { setGen(true); try { const { data } = await api.post(`/studio/${studio}/${id}/brief/generate`); setF(data); toast.success("Brief generado con IA"); onSaved(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setGen(false); } };
  return (
    <div className="bg-white border border-navy/10 rounded-lg p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="overline text-navy/40">Brief estratégico</div>
        <button data-testid="brief-generate" onClick={generate} disabled={gen} className="inline-flex items-center gap-2 text-sm rounded-full border border-violet/30 text-violet px-4 py-2 hover:bg-violet/5 transition-colors disabled:opacity-50">
          {gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar con IA
        </button>
      </div>
      {fields.map(([k, l]) => (
        <div key={k}><label className="text-xs text-navy/50 mb-1 block">{l}</label>
          <textarea data-testid={`brief-${k}`} rows={2} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={input} /></div>
      ))}
      <button data-testid="brief-save" onClick={save} disabled={busy} className="rounded-full bg-gradient-violet text-white px-6 py-2.5 text-sm hover:opacity-90 transition-opacity disabled:opacity-50">{busy ? "Guardando…" : "Guardar brief"}</button>
    </div>
  );
}

/* ---------------- Phases ---------------- */
function Phases({ studio, id, p, onChange }) {
  const STAT = [["pending", "Pendiente", "bg-navy/10 text-navy/50"], ["in_progress", "En curso", "bg-violet/10 text-violet"], ["done", "Completada", "bg-emerald-500/10 text-emerald-600"]];
  const set = async (key, status) => { try { await api.patch(`/studio/${studio}/${id}/phases/${key}`, { status }); onChange(); } catch (e) { toast.error("Error"); } };
  return (
    <div className="space-y-2">
      {(p.phases || []).map((ph, i) => (
        <div key={ph.key} data-testid={`phase-${ph.key}`} className="flex items-center gap-4 bg-white border border-navy/10 rounded-lg px-5 py-4">
          <div className="font-editorial text-2xl text-violet/40 w-8">{String(i + 1).padStart(2, "0")}</div>
          <div className="flex-1 font-medium">{ph.label}</div>
          <div className="flex gap-1">
            {STAT.map(([v, l, cls]) => (
              <button key={v} data-testid={`phase-${ph.key}-${v}`} onClick={() => set(ph.key, v)}
                className={`text-[11px] px-2.5 py-1 rounded-full transition-all ${ph.status === v ? cls : "bg-transparent text-navy/40 hover:bg-navy/5"}`}>{l}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Board (Kanban) ---------------- */
function Board({ studio, id, data, onChange }) {
  const [adding, setAdding] = useState("");
  const [title, setTitle] = useState("");
  const add = async (status) => {
    if (!title.trim()) return;
    try { await api.post(`/studio/${studio}/${id}/tasks`, { title: title.trim(), status }); setTitle(""); setAdding(""); onChange(); } catch (e) { toast.error("Error"); }
  };
  const move = async (t, status) => { try { await api.patch(`/studio/${studio}/tasks/${t.id}`, { status }); onChange(); } catch (e) { toast.error("Error"); } };
  const assign = async (t, m) => { try { await api.patch(`/studio/${studio}/tasks/${t.id}`, { assignee_id: m?.id || "", assignee: m?.name || "" }); onChange(); } catch (e) { toast.error("Error"); } };
  const setDate = async (t, field, value) => { try { await api.patch(`/studio/${studio}/tasks/${t.id}`, { [field]: value }); onChange(); } catch (e) { toast.error("Error"); } };
  const members = data.members || [];
  const del = async (t) => { try { await api.delete(`/studio/${studio}/tasks/${t.id}`); onChange(); } catch (e) { toast.error("Error"); } };
  return (
    <div className="grid md:grid-cols-5 gap-3">
      {COLS.map(([st, lbl]) => {
        const items = data.tasks.filter((t) => t.status === st);
        return (
          <div key={st} data-testid={`board-col-${st}`} className="bg-navy/[0.03] rounded-lg p-3 min-h-[120px]">
            <div className="flex items-center justify-between mb-2"><span className="text-xs font-medium text-navy/60">{lbl}</span><span className="text-xs text-navy/30">{items.length}</span></div>
            <div className="space-y-2">
              {items.map((t) => (
                <div key={t.id} data-testid={`task-${t.id}`} className="bg-white border border-navy/10 rounded-lg p-3 group">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm">{t.title}</span>
                    <button onClick={() => del(t)} className="text-navy/20 hover:text-destructive opacity-0 group-hover:opacity-100 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {t.priority && <span className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full ${PRIO[t.priority]}`}>{t.priority}</span>}
                  <select data-testid={`task-assign-${t.id}`} value={t.assignee_id || ""} onChange={(e) => assign(t, members.find((m) => m.id === e.target.value))} className="mt-2 w-full text-[11px] bg-cream border border-navy/10 rounded px-1.5 py-1 outline-none">
                    <option value="">Sin responsable</option>
                    {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <select data-testid={`task-move-${t.id}`} value={t.status} onChange={(e) => move(t, e.target.value)} className="mt-1.5 w-full text-[11px] bg-cream border border-navy/10 rounded px-1.5 py-1 outline-none">
                    {COLS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <div className="mt-1.5 grid grid-cols-2 gap-1">
                    <label className="text-[9px] text-navy/40 uppercase tracking-wide">Inicio
                      <input data-testid={`task-start-${t.id}`} type="date" value={t.start_date || ""} onChange={(e) => setDate(t, "start_date", e.target.value)} className="w-full text-[11px] bg-cream border border-navy/10 rounded px-1 py-0.5 outline-none" /></label>
                    <label className="text-[9px] text-navy/40 uppercase tracking-wide">Entrega
                      <input data-testid={`task-due-${t.id}`} type="date" value={t.due_date || ""} onChange={(e) => setDate(t, "due_date", e.target.value)} className="w-full text-[11px] bg-cream border border-navy/10 rounded px-1 py-0.5 outline-none" /></label>
                  </div>
                  <div className="mt-1 grid grid-cols-2 gap-1">
                    <label className="text-[9px] text-navy/40 uppercase tracking-wide">Hora inicio
                      <input data-testid={`task-start-time-${t.id}`} type="time" value={t.start_time || ""} onChange={(e) => setDate(t, "start_time", e.target.value)} className="w-full text-[11px] bg-cream border border-navy/10 rounded px-1 py-0.5 outline-none" /></label>
                    <label className="text-[9px] text-navy/40 uppercase tracking-wide">Hora fin
                      <input data-testid={`task-end-time-${t.id}`} type="time" value={t.end_time || ""} onChange={(e) => setDate(t, "end_time", e.target.value)} className="w-full text-[11px] bg-cream border border-navy/10 rounded px-1 py-0.5 outline-none" /></label>
                  </div>
                </div>
              ))}
              {adding === st ? (
                <div className="space-y-1.5">
                  <input autoFocus data-testid={`board-input-${st}`} value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add(st)} placeholder="Título…" className="w-full text-sm bg-white border border-navy/10 rounded px-2 py-1.5 outline-none focus:border-violet" />
                  <div className="flex gap-1"><button onClick={() => add(st)} className="text-xs bg-violet text-white rounded px-2 py-1">Añadir</button><button onClick={() => { setAdding(""); setTitle(""); }} className="text-xs text-navy/40 px-2">Cancelar</button></div>
                </div>
              ) : (
                <button data-testid={`board-add-${st}`} onClick={() => { setAdding(st); setTitle(""); }} className="w-full text-xs text-navy/40 hover:text-violet flex items-center gap-1 py-1.5 transition-colors"><Plus className="w-3.5 h-3.5" /> Añadir</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Timeline ---------------- */
function Timeline({ data }) {
  const items = [...data.tasks].filter((t) => t.due_date || t.start_date).sort((a, b) => (a.due_date || a.start_date || "9999").localeCompare(b.due_date || b.start_date || "9999"));
  if (items.length === 0) return <div className="text-navy/40 py-10 text-center border border-dashed border-navy/15 rounded-lg">Añade fechas a las tareas (en el Tablero) para verlas en el cronograma.</div>;
  return (
    <div className="space-y-2">
      {items.map((t) => (
        <div key={t.id} className="flex items-center gap-4 bg-white border border-navy/10 rounded-lg px-5 py-3" data-testid={`timeline-${t.id}`}>
          <CalendarRange className="w-4 h-4 text-violet shrink-0" />
          <div className="flex-1 text-sm font-medium">{t.title}</div>
          <div className="text-xs text-navy/50">{t.start_date || "—"} → {t.due_date || "—"}</div>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-navy/10 text-navy/60">{(COLS.find((c) => c[0] === t.status) || [])[1]}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Sprints ---------------- */
function Sprints({ studio, id, data, onChange }) {
  const [f, setF] = useState({ name: "", goal: "", start_date: "", end_date: "" });
  const add = async () => { if (!f.name.trim()) return; try { await api.post(`/studio/${studio}/${id}/sprints`, f); setF({ name: "", goal: "", start_date: "", end_date: "" }); onChange(); } catch (e) { toast.error("Error"); } };
  return (
    <div className="space-y-4">
      <div className="bg-white border border-navy/10 rounded-lg p-4 grid md:grid-cols-5 gap-2 items-end">
        <input data-testid="sprint-name" placeholder="Nombre del sprint" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${input} md:col-span-2`} />
        <input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={input} />
        <input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className={input} />
        <button data-testid="sprint-add" onClick={add} className="rounded-full bg-gradient-violet text-white text-sm py-2.5 hover:opacity-90 transition-opacity">Crear</button>
      </div>
      {data.sprints.length === 0 ? <div className="text-navy/40 py-6 text-center text-sm">Sin sprints todavía.</div> : (
        <div className="space-y-2">{data.sprints.map((s) => (
          <div key={s.id} className="bg-white border border-navy/10 rounded-lg px-5 py-3">
            <div className="font-medium text-sm">{s.name}</div>
            <div className="text-xs text-navy/45">{s.start_date || "—"} → {s.end_date || "—"}{s.goal ? ` · ${s.goal}` : ""}</div>
          </div>
        ))}</div>
      )}
    </div>
  );
}

/* ---------------- Quote ---------------- */
function Quote({ studio, id, p, data, onSaved }) {
  const q = p.quote || {};
  const [mode, setMode] = useState("sale"); // sale = de nosotros al cliente | supplier = de un proveedor hacia nosotros
  const [items, setItems] = useState([]);
  const [margin, setMargin] = useState(0);
  const [tax, setTax] = useState(0);
  const [marginIncluded, setMarginIncluded] = useState(false);
  const [taxIncluded, setTaxIncluded] = useState(false);
  const [supplier, setSupplier] = useState("");
  const [concept, setConcept] = useState("");
  const [terms, setTerms] = useState({});
  const setT = (k, v) => setTerms((t) => ({ ...t, [k]: v }));
  const [folio, setFolio] = useState("");
  const [hideTotal, setHideTotal] = useState(false);
  const [imp, setImp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  // Documentos de cotización de venta (múltiples por proyecto)
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState(null);          // documento abierto en el editor (null = nueva)
  const [title, setTitle] = useState("");
  const [currency, setCurrency] = useState(q.currency || p.currency || "MXN");
  const currentFolio = folio || "";
  const impRef = useRef();
  const studioLabel = STUDIO_LABEL[studio] || studio.toUpperCase();
  const openDoc = docs.find((d) => d.id === docId) || null;

  const supplierQuotes = (data?.entries || []).filter((e) => e.type === "supplier_quote");
  const minQuote = supplierQuotes.length ? Math.min(...supplierQuotes.map((e) => Number(e.amount || 0))) : 0;

  const addItem = () => setItems([...items, { id: String(Date.now()), description: "", qty: 1, unit_price: 0 }]);
  const upd = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rm = (i) => setItems(items.filter((_, idx) => idx !== i));
  const subtotal = items.filter((it) => it.kind !== "header").reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0), 0);
  const _base = subtotal + (marginIncluded ? 0 : subtotal * margin / 100);
  const saleTotal = taxIncluded ? _base : _base * (1 + tax / 100);

  const saleLocked = mode === "sale" && !!openDoc && (openDoc.status === "approved" || openDoc.invoiced);

  const loadDocs = useCallback(async () => {
    try { const { data: r } = await api.get(`/studio/${studio}/${id}/quotes`); setDocs(r || []); return r || []; }
    catch { return []; }
  }, [studio, id]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const clearSale = () => {
    setDocId(null); setItems([]); setMargin(0); setTax(0); setMarginIncluded(false);
    setTaxIncluded(false); setTerms(templateTerms || {}); setFolio(""); setHideTotal(false); setTitle("");
  };
  const openSaleDoc = (d) => {
    const dq = d.quote || {};
    setMode("sale"); setDocId(d.id); setTitle(d.title || "");
    setItems((dq.items || []).map((it) => ({ id: it.id || String(Math.random()), ...it })));
    setMargin(dq.margin_pct || 0); setTax(dq.tax_pct || 0);
    setMarginIncluded(!!dq.margin_included); setTaxIncluded(!!dq.tax_included);
    setTerms(dq.terms || {}); setFolio(dq.folio || ""); setHideTotal(!!dq.hide_total);
    setCurrency(dq.currency || "MXN");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const salePayload = () => ({ title, items, margin_pct: Number(margin), tax_pct: Number(tax), margin_included: marginIncluded, tax_included: taxIncluded, terms, folio, hide_total: hideTotal, currency });

  // Guarda el documento (crea o actualiza). Devuelve el id del documento o null.
  const saveSale = async ({ keepOpen = false } = {}) => {
    if (saleLocked) return docId;  // aprobado/facturado: no se modifica
    if (!items.length) { toast.error("Agrega al menos una partida"); return null; }
    try {
      if (docId) {
        const { data: r } = await api.put(`/studio/${studio}/${id}/quotes/${docId}`, salePayload());
        toast.success("Cotización actualizada"); await loadDocs(); onSaved();
        if (!keepOpen) clearSale();
        return r.id;
      }
      const { data: r } = await api.post(`/studio/${studio}/${id}/quotes`, salePayload());
      toast.success(`Cotización guardada · ${r.folio || r.title}`); await loadDocs(); onSaved();
      if (keepOpen) { setDocId(r.id); } else { clearSale(); }
      return r.id;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); return null; }
  };
  const deleteDoc = async (d) => {
    if (!window.confirm(`¿Eliminar la cotización "${d.title || d.folio}"?`)) return;
    try { await api.delete(`/studio/${studio}/${id}/quotes/${d.id}`); toast.success("Cotización eliminada"); if (docId === d.id) clearSale(); await loadDocs(); onSaved(); }
    catch { toast.error("No se pudo eliminar"); }
  };
  const openInvoiceStudio = async () => {
    if (!items.some((it) => it.kind !== "header" && Number(it.unit_price) > 0)) { toast.error("Agrega partidas con importe antes de facturar"); return; }
    const qid = await saveSale({ keepOpen: true });
    if (qid) setInvoiceOpen(true);
  };
  const shareQuoteLink = async () => {
    const qid = await saveSale({ keepOpen: true });
    if (!qid) return;
    try {
      const { data } = await api.post(`/studio/${studio}/${id}/quotes/${qid}/share-link`);
      const full = window.location.origin + data.path;
      await loadDocs();
      try { await navigator.clipboard.writeText(full); toast.success("Enlace de la cotización copiado"); }
      catch { toast.success("Enlace generado: " + full); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Guarda una cotización con partidas primero"); }
  };
  const saveSupplier = async () => {
    if (!supplier.trim()) return toast.error("Indica el nombre del proveedor");
    if (!items.length) return toast.error("Agrega al menos un ítem a la cotización");
    setSaving(true);
    try {
      if (editingSupplier) {
        await api.patch(`/studio/${studio}/finance/entries/${editingSupplier}`, {
          supplier: supplier.trim(), concept: concept.trim() || `Cotización de ${supplier.trim()}`,
          amount: subtotal, items, terms, date: terms.fecha || "",
        });
        toast.success("Cotización de proveedor actualizada");
      } else {
        await api.post(`/studio/${studio}/${id}/finance/entries`, {
          type: "supplier_quote", supplier: supplier.trim(),
          concept: concept.trim() || `Cotización de ${supplier.trim()}`,
          amount: subtotal, items, terms, status: "pending",
          date: terms.fecha || "",
        });
        toast.success("Cotización de proveedor guardada · disponible en Finanzas (gasto futuro)");
      }
      setItems([]); setSupplier(""); setConcept(""); setTerms({}); setEditingSupplier(null); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setSaving(false); }
  };
  const editSupplierQuote = (e) => {
    setMode("supplier");
    setSupplier(e.supplier || "");
    setConcept(e.concept || "");
    setItems((e.items || []).map((it) => ({ id: it.id || String(Math.random()), ...it })));
    setTerms(e.terms || {});
    setEditingSupplier(e.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Editando la cotización · guarda para actualizar");
  };
  const cancelEditSupplier = () => {
    setEditingSupplier(null); setItems([]); setSupplier(""); setConcept(""); setTerms({});
  };
  const delSupplierQuote = async (e) => {
    if (!window.confirm(`¿Eliminar la cotización de ${e.supplier || "proveedor"}?`)) return;
    try { await api.delete(`/studio/${studio}/finance/entries/${e.id}`); toast.success("Cotización eliminada"); onSaved(); }
    catch { toast.error("Error"); }
  };
  const convertSupplierQuote = async (e) => {
    if (e.converted) { toast.info("Esta cotización ya se convirtió en gasto"); return; }
    try {
      await api.post(`/studio/${studio}/finance/entries/${e.id}/convert`);
      toast.success(`Convertida a gasto (Pago a proveedor): ${e.supplier || e.concept} · la cotización se conserva en el expediente`); onSaved();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "No se pudo convertir"); }
  };
  const [attaching, setAttaching] = useState(null);
  const [preview, setPreview] = useState(null); // { url, name, type, loading }
  const attachFile = async (e, ev) => {
    const file = ev.target.files?.[0]; if (!file) { return; }
    setAttaching(e.id);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/studio/${studio}/finance/entries/${e.id}/attach`, fd); toast.success("PDF adjuntado a la cotización"); onSaved(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "No se pudo adjuntar"); }
    finally { setAttaching(null); ev.target.value = ""; }
  };
  const openPreview = async (e, a) => {
    setPreview({ loading: true, name: a.filename, type: a.content_type || "", entry: e.id, aid: a.id });
    try {
      const res = await api.get(`/studio/${studio}/finance/entries/${e.id}/attachment/${a.id}`, { responseType: "blob" });
      const type = a.content_type || res.data.type || (String(a.kind).toLowerCase() === "pdf" ? "application/pdf" : "");
      const url = URL.createObjectURL(new Blob([res.data], { type: type || "application/octet-stream" }));
      setPreview({ url, name: a.filename, type, entry: e.id, aid: a.id });
    } catch { toast.error("No se pudo abrir el archivo"); setPreview(null); }
  };
  const closePreview = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); };
  const openEntryPdf = async (e) => {
    setPreview({ loading: true, name: `Cotizacion-${e.supplier || "proveedor"}.pdf`, type: "application/pdf" });
    try {
      const res = await api.get(`/studio/${studio}/finance/entries/${e.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPreview({ url, name: `Cotizacion-${e.supplier || "proveedor"}.pdf`, type: "application/pdf" });
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "No se pudo generar el PDF"); setPreview(null); }
  };

  // Plantilla de condiciones (por tenant) + prellenado en cotización nueva
  const [templateTerms, setTemplateTerms] = useState(null);
  const [accent, setAccent] = useState("#7c5cff");
  useEffect(() => {
    api.get(`/studio/quote-terms-template`).then((r) => {
      const t = r.data?.terms || {};
      setTemplateTerms(t);
      if (r.data?.accent_color) setAccent(r.data.accent_color);
      if (Object.keys(q.terms || {}).length === 0 && Object.keys(t).length > 0) setTerms(t);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const saveAccent = async (color) => {
    setAccent(color);
    try { await api.put(`/studio/quote-terms-template`, { accent_color: color }); } catch { /* silencioso */ }
  };
  const saveTemplate = async () => {
    try { await api.put(`/studio/quote-terms-template`, { terms }); setTemplateTerms(terms); toast.success("Plantilla de condiciones guardada"); }
    catch { toast.error("No se pudo guardar la plantilla"); }
  };
  const useTemplate = () => {
    if (templateTerms && Object.keys(templateTerms).length) { setTerms({ ...terms, ...templateTerms }); toast.success("Plantilla aplicada"); }
    else toast.error("Aún no tienes una plantilla guardada");
  };
  const exportPdf = async () => {
    const qid = await saveSale({ keepOpen: true });
    if (!qid) return;
    try {
      setPreview({ loading: true, name: "Cotización.pdf", type: "application/pdf" });
      const res = await api.get(`/studio/${studio}/${id}/quotes/${qid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      setPreview({ url, name: `Cotizacion-${title || p.name || "proyecto"}.pdf`, type: "application/pdf" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar el PDF"); setPreview(null); }
  };
  const importQuote = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return; setImp(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data: r } = await api.post(`/studio/${studio}/${id}/quote/import`, fd); setItems(r.items || []); if (mode === "sale") { setMargin(r.margin_pct || 0); setTax(r.tax_pct || 0); } toast.success("Cotización importada"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo leer la cotización"); } finally { setImp(false); if (impRef.current) impRef.current.value = ""; }
  };

  // Logo de empresa (por tenant) que aparece en el PDF de la cotización
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const logoRef = useRef();
  const loadLogo = useCallback(async () => {
    try {
      const res = await api.get(`/studio/quote-logo`, { responseType: "blob" });
      setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
    } catch { setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; }); }
  }, []);
  useEffect(() => { loadLogo(); }, [loadLogo]);
  const uploadLogo = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    setLogoBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/studio/quote-logo`, fd); toast.success("Logo actualizado · aparecerá en el PDF"); await loadLogo(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir el logo"); }
    finally { setLogoBusy(false); if (logoRef.current) logoRef.current.value = ""; }
  };
  const removeLogo = async () => {
    if (!window.confirm("¿Quitar el logo de las cotizaciones?")) return;
    try { await api.delete(`/studio/quote-logo`); toast.success("Logo eliminado"); setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; }); }
    catch { toast.error("No se pudo eliminar el logo"); }
  };

  // Enviar cotización por correo al cliente (PDF adjunto)
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState(p.client_email || "");
  const [emailMsg, setEmailMsg] = useState("");
  const [sending, setSending] = useState(false);
  const sendEmail = async () => {
    if (!emailTo.trim()) return toast.error("Escribe el correo del cliente");
    setSending(true);
    try {
      const qid = await saveSale({ keepOpen: true });
      if (!qid) { setSending(false); return; }
      const { data } = await api.post(`/studio/${studio}/${id}/quotes/${qid}/email`, { to: emailTo.trim(), message: emailMsg.trim() });
      toast.success(`Cotización enviada a ${data.to}`);
      setEmailOpen(false); setEmailMsg(""); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo enviar el correo"); }
    finally { setSending(false); }
  };

  const modeBtn = (m, Icon, title, sub) => (
    <button data-testid={`quote-mode-${m}`} onClick={() => setMode(m)}
      className={`flex-1 text-left rounded-xl border p-3 transition-colors ${mode === m ? "border-violet bg-violet/5" : "border-navy/10 hover:border-navy/25"}`}>
      <div className={`flex items-center gap-2 text-sm font-medium ${mode === m ? "text-violet" : "text-navy"}`}><Icon className="w-4 h-4" /> {title}</div>
      <div className="text-xs text-navy/45 mt-0.5">{sub}</div>
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="bg-white border border-navy/10 rounded-lg p-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-2" data-testid="quote-mode-selector">
          {modeBtn("sale", DollarSign, `De ${studioLabel} → cliente`, "Cotización de venta (ingreso). Con margen e impuesto.")}
          {modeBtn("supplier", Building2, `De proveedor → ${studioLabel}`, "Cotización de un proveedor (gasto futuro nuestro).")}
        </div>

        {mode === "sale" && (
          <div className="space-y-3" data-testid="quote-docs-bar">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-sm font-medium text-navy">
                <FileText className="w-4 h-4 text-violet" />
                {docId ? "Editando cotización" : "Nueva cotización"}
                {currentFolio && <span className="px-2.5 py-1 rounded-full bg-violet/10 text-violet text-xs font-medium tracking-wide" data-testid="quote-folio">{currentFolio}</span>}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-navy/50">Moneda</span>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="bg-white border border-navy/10 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-violet" data-testid="quote-currency">
                    <option value="MXN">MXN · Peso</option>
                    <option value="USD">USD · Dólar</option>
                    <option value="EUR">EUR · Euro</option>
                  </select>
                </div>
                {docId && <button data-testid="quote-new-doc" onClick={clearSale} className="inline-flex items-center gap-1.5 text-sm rounded-full border border-navy/15 px-4 py-2 hover:border-violet hover:text-violet transition-colors"><Plus className="w-4 h-4" /> Nueva cotización</button>}
              </div>
            </div>
            <input placeholder="Nombre de la cotización (opcional, ej. Propuesta v2)" value={title} onChange={(e) => setTitle(e.target.value)} className={input} data-testid="quote-title" />

            {docs.length > 0 && (
              <div className="rounded-xl border border-navy/10 divide-y divide-navy/5" data-testid="quote-docs-list">
                {docs.map((d) => (
                  <div key={d.id} className={`flex items-center justify-between gap-2 px-3 py-2.5 flex-wrap ${docId === d.id ? "bg-violet/[0.04]" : ""}`} data-testid={`quote-doc-${d.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-navy truncate">{d.title || d.folio || "Cotización"}
                        {d.status === "approved" && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600">Aprobada</span>}
                        {d.invoiced && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600">Facturada</span>}
                      </div>
                      <div className="text-xs text-navy/45">{d.folio ? d.folio + " · " : ""}{money(d.total, d.currency)}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`quote-doc-open-${d.id}`} onClick={() => openSaleDoc(d)} className="text-xs rounded-lg border border-navy/15 px-3 py-1.5 hover:border-violet hover:text-violet transition-colors">Abrir</button>
                      <button data-testid={`quote-doc-del-${d.id}`} onClick={() => deleteDoc(d)} className="text-navy/30 hover:text-destructive p-1.5"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-end justify-between gap-3">
          {mode === "supplier" && (
            <div className="flex-1 min-w-[220px]">
              <label className="text-xs text-navy/50 block mb-1">Proveedor</label>
              <input placeholder="Nombre del proveedor" value={supplier} onChange={(e) => setSupplier(e.target.value)} className={input} data-testid="quote-supplier-name" />
            </div>
          )}
          <div className="ml-auto">
            <input ref={impRef} type="file" accept=".pdf,.xml,.txt,.csv" className="hidden" data-testid="quote-import-input" onChange={importQuote} />
            <button data-testid="quote-import" onClick={() => impRef.current?.click()} disabled={imp} className="inline-flex items-center gap-1.5 text-sm rounded-full border border-navy/15 px-4 py-2 hover:border-violet hover:text-violet transition-colors disabled:opacity-50">
              {imp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Importar cotización (PDF)
            </button>
          </div>
        </div>

        {mode === "supplier" && (
          <input placeholder="Concepto / referencia (opcional)" value={concept} onChange={(e) => setConcept(e.target.value)} className={input} data-testid="quote-supplier-concept" />
        )}

        {saleLocked && (
          <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-700 flex items-center gap-2" data-testid="quote-locked">
            <DollarSign className="w-4 h-4 shrink-0" /> {openDoc?.status === "approved" ? "El cliente ya aprobó esta cotización" : "Esta cotización ya generó facturas (ingresos)"}; ya no se puede modificar. Puedes exportarla, compartir el enlace o facturarla.
          </div>
        )}

        {mode === "sale" ? (
          <QuoteBuilder
            value={{ items, margin_pct: margin, tax_pct: tax, margin_included: marginIncluded, tax_included: taxIncluded, hide_total: hideTotal }}
            readOnly={saleLocked}
            onChange={(nv) => { setItems(nv.items); setMargin(nv.margin_pct); setTax(nv.tax_pct); setMarginIncluded(nv.margin_included); setTaxIncluded(nv.tax_included); setHideTotal(nv.hide_total); }} />
        ) : (
          <>
            {items.map((it, i) => (
              <div key={it.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`quote-item-${i}`}>
                <input placeholder="Concepto" value={it.description} onChange={(e) => upd(i, "description", e.target.value)} className={`${input} col-span-6`} />
                <input type="number" placeholder="Cant." value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} className={`${input} col-span-2`} />
                <input type="number" placeholder="Precio" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className={`${input} col-span-3`} />
                <button onClick={() => rm(i)} className="col-span-1 text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button data-testid="quote-add-item" onClick={addItem} className="text-sm text-violet hover:underline flex items-center gap-1"><Plus className="w-4 h-4" /> Añadir concepto</button>
            <div className="flex flex-wrap gap-4 pt-3 border-t border-navy/10 items-end">
              <div className="ml-auto text-right"><div className="text-xs text-navy/50">Total</div><div className="text-2xl font-medium" data-testid="quote-total">{money(subtotal)}</div></div>
            </div>
          </>
        )}

        <fieldset disabled={saleLocked} className="pt-3 border-t border-navy/10 space-y-3 min-w-0 p-0 m-0" data-testid="quote-terms">
          {mode === "sale" && <>
            <QuoteHeaderFields terms={terms} onTerm={setT} folio={folio} onFolio={setFolio} currentFolio={currentFolio} />
            <div className="pt-3 border-t border-navy/10" />
          </>}
          <div className="text-sm font-medium text-navy flex items-center gap-1.5"><FileText className="w-4 h-4 text-violet" /> Condiciones de la cotización</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-navy/50 block mb-1">Fecha de cotización</label>
              <input type="date" className={input} value={terms.fecha || ""} onChange={(e) => setT("fecha", e.target.value)} data-testid="quote-terms-date" />
            </div>
            <div>
              <label className="text-xs text-navy/50 block mb-1">Condiciones de pago</label>
              <input className={input} placeholder="Ej. 50% anticipo, 50% contra entrega" value={terms.payment_terms || ""} onChange={(e) => setT("payment_terms", e.target.value)} data-testid="quote-terms-payment" />
            </div>
          </div>
          <div>
            <label className="text-xs text-navy/50 block mb-1">Cuentas / datos donde se debe pagar</label>
            <textarea rows={2} className={input} placeholder="Banco, titular, CLABE / número de cuenta, referencia…" value={terms.payment_accounts || ""} onChange={(e) => setT("payment_accounts", e.target.value)} data-testid="quote-terms-accounts" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-navy/50 block mb-1">Garantía</label>
              <input className={input} placeholder="Ej. 12 meses en mano de obra" value={terms.warranty || ""} onChange={(e) => setT("warranty", e.target.value)} data-testid="quote-terms-warranty" />
            </div>
            <div>
              <label className="text-xs text-navy/50 block mb-1">Vigencia</label>
              <input className={input} placeholder="Ej. 15 días" value={terms.validity || ""} onChange={(e) => setT("validity", e.target.value)} data-testid="quote-terms-validity" />
            </div>
          </div>
          <div>
            <label className="text-xs text-navy/50 block mb-1">Otras condiciones / notas</label>
            <textarea rows={2} className={input} placeholder="Condiciones generales, tiempos de entrega, exclusiones, etc." value={terms.conditions || ""} onChange={(e) => setT("conditions", e.target.value)} data-testid="quote-terms-notes" />
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-navy/10">
            <div className="text-xs text-navy/50 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5 text-violet" /> Logo en el PDF</div>
            {logoUrl
              ? <img src={logoUrl} alt="Logo" className="h-10 max-w-[130px] object-contain rounded border border-navy/10 bg-white p-1" data-testid="quote-logo-preview" />
              : <span className="text-xs text-navy/40">Sin logo</span>}
            <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="quote-logo-input" onChange={uploadLogo} />
            <button type="button" onClick={() => logoRef.current?.click()} disabled={logoBusy} data-testid="quote-logo-upload" className="text-xs text-violet hover:underline inline-flex items-center gap-1 disabled:opacity-50">
              {logoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />} {logoUrl ? "Cambiar logo" : "Subir logo"}
            </button>
            {logoUrl && <button type="button" onClick={removeLogo} data-testid="quote-logo-remove" className="text-xs text-navy/40 hover:text-destructive inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Quitar</button>}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-navy/50">Color de marca (PDF y enlace)</span>
              <input type="color" value={accent} onChange={(e) => saveAccent(e.target.value)} data-testid="quote-accent-color" className="w-8 h-8 rounded cursor-pointer border border-navy/10 bg-white p-0.5" title="Color de marca" />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button type="button" onClick={saveTemplate} className="text-xs text-violet hover:underline inline-flex items-center gap-1" data-testid="quote-terms-save-template"><Check className="w-3.5 h-3.5" /> Guardar como plantilla</button>
            <button type="button" onClick={useTemplate} className="text-xs text-navy/50 hover:text-violet inline-flex items-center gap-1" data-testid="quote-terms-use-template"><RefreshCw className="w-3.5 h-3.5" /> Usar mi plantilla</button>
          </div>
        </fieldset>

        {mode === "sale"
          ? <>
              <div className="flex flex-wrap items-center gap-2">
                {!saleLocked && <button data-testid="quote-save" onClick={() => saveSale()} className="rounded-full bg-gradient-violet text-white px-6 py-2.5 text-sm hover:opacity-90 transition-opacity">{docId ? "Actualizar cotización" : "Guardar cotización"}</button>}
                <button data-testid="quote-export-pdf" onClick={exportPdf} className="rounded-full border border-navy/15 px-5 py-2.5 text-sm hover:border-violet hover:text-violet transition-colors inline-flex items-center gap-1.5"><FileText className="w-4 h-4" /> Exportar PDF</button>
                <button data-testid="quote-email-open" onClick={() => { setEmailTo(p.client_email || emailTo); setEmailOpen(true); }} className="rounded-full border border-navy/15 px-5 py-2.5 text-sm hover:border-violet hover:text-violet transition-colors inline-flex items-center gap-1.5"><Send className="w-4 h-4" /> Enviar por correo</button>
                <button data-testid="quote-share-link" onClick={shareQuoteLink} className="rounded-full border border-navy/15 px-5 py-2.5 text-sm hover:border-violet hover:text-violet transition-colors inline-flex items-center gap-1.5"><Link2 className="w-4 h-4" /> Enviar por enlace</button>
                <button data-testid="quote-invoice-open" onClick={openInvoiceStudio} className="rounded-full bg-gradient-violet text-white px-5 py-2.5 text-sm hover:opacity-90 transition-opacity inline-flex items-center gap-1.5"><ReceiptText className="w-4 h-4" /> Facturar presupuesto</button>
              </div>
              {docId && <div className="mt-6 pt-5 border-t border-navy/10">
                <QuoteComments path={`/studio/${studio}/${id}/quotes/${docId}/comments`} role="company" accent={accent} />
              </div>}
            </>
          : <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <button data-testid="quote-save-supplier" onClick={saveSupplier} disabled={saving} className="rounded-full bg-gradient-violet text-white px-6 py-2.5 text-sm hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center gap-2">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editingSupplier ? "Actualizar cotización" : "Guardar cotización de proveedor"}
                </button>
                {editingSupplier && <button data-testid="quote-cancel-supplier" onClick={cancelEditSupplier} className="text-xs text-navy/50 hover:text-destructive">Cancelar edición</button>}
              </div>
              <p className="text-xs text-navy/45">{editingSupplier ? "Estás editando una cotización existente." : "Se guarda como "}{!editingSupplier && <b>gasto futuro</b>}{!editingSupplier && ". Compárala abajo con otros proveedores y conviértela en gasto."}</p>
            </div>}
      </div>

      {supplierQuotes.length > 0 && (
        <div className="bg-white border border-navy/10 rounded-lg p-5" data-testid="quote-supplier-compare">
          <div className="flex items-center gap-2 mb-3">
            <GitCompare className="w-4 h-4 text-violet" />
            <span className="text-sm font-medium">Cotizaciones de proveedores del proyecto</span>
            <span className="text-[11px] text-navy/40">· {supplierQuotes.length} {supplierQuotes.length === 1 ? "cotización" : "cotizaciones"}{supplierQuotes.length >= 2 ? " · la más baja marcada" : ""}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...supplierQuotes].sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0)).map((e) => {
              const best = supplierQuotes.length >= 2 && Number(e.amount || 0) === minQuote;
              return (
                <div key={e.id} data-testid={`quote-compare-${e.id}`}
                  className={`rounded-lg border p-3 flex flex-col gap-1.5 ${best ? "border-emerald-500 bg-emerald-500/[0.04]" : "border-navy/10"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{e.supplier || "Proveedor"}</span>
                    {best && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 shrink-0">Más baja</span>}
                  </div>
                  <div className="text-xs text-navy/50 truncate">{e.concept || "—"}</div>
                  {(e.items || []).length > 0 && <div className="text-[11px] text-navy/40">{e.items.length} {e.items.length === 1 ? "ítem" : "ítems"}</div>}
                  {e.terms?.fecha && <div className="text-[11px] text-navy/40">Fecha: {e.terms.fecha}</div>}
                  {e.terms?.payment_terms && <div className="text-[11px] text-navy/40 truncate">Pago: {e.terms.payment_terms}</div>}
                  {e.terms?.warranty && <div className="text-[11px] text-navy/40 truncate">Garantía: {e.terms.warranty}</div>}
                  <div className="text-lg font-medium mt-auto">{money(e.amount)}</div>
                  {(e.attachments || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {e.attachments.map((a) => (
                        <button key={a.id} data-testid={`quote-compare-att-${a.id}`} onClick={() => openPreview(e, a)}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-violet/10 text-violet hover:bg-violet/20 transition-colors max-w-full">
                          <FileText className="w-3 h-3 shrink-0" /> <span className="truncate">{a.filename}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    {(e.items || []).length > 0 && (
                      <button data-testid={`quote-compare-view-${e.id}`} onClick={() => openEntryPdf(e)}
                        className="inline-flex items-center gap-1.5 text-xs rounded-full bg-navy text-white px-3 py-1.5 hover:opacity-90 transition-opacity">
                        <FileText className="w-3.5 h-3.5" /> Ver PDF
                      </button>
                    )}
                    <label data-testid={`quote-compare-attach-${e.id}`} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-violet hover:text-violet transition-colors cursor-pointer">
                      {attaching === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} Adjuntar
                      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.xml" className="hidden" onChange={(ev) => attachFile(e, ev)} />
                    </label>
                    <button data-testid={`quote-compare-edit-${e.id}`} onClick={() => editSupplierQuote(e)}
                      className="inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-violet hover:text-violet transition-colors">
                      <FileText className="w-3.5 h-3.5" /> Editar
                    </button>
                    {e.converted
                      ? <span data-testid={`quote-compare-converted-${e.id}`} className="inline-flex items-center gap-1.5 text-xs rounded-full bg-emerald-500/10 text-emerald-600 px-3 py-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Convertida a gasto</span>
                      : <button data-testid={`quote-compare-convert-${e.id}`} onClick={() => convertSupplierQuote(e)}
                          className="inline-flex items-center justify-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-emerald-500 hover:text-emerald-600 transition-colors">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Gasto
                        </button>}
                    <button data-testid={`quote-compare-del-${e.id}`} onClick={() => delSupplierQuote(e)} className="text-navy/30 hover:text-destructive ml-auto"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-[70] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={closePreview} data-testid="quote-preview-modal">
          <div className="bg-white rounded-xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden shadow-2xl" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-navy/10">
              <FileText className="w-4 h-4 text-violet shrink-0" />
              <span className="text-sm font-medium truncate flex-1" data-testid="quote-preview-name">{preview.name}</span>
              {preview.url && <a href={preview.url} download={preview.name} className="text-navy/40 hover:text-violet inline-flex items-center gap-1 text-xs" data-testid="quote-preview-download"><Download className="w-4 h-4" /> Descargar</a>}
              <button onClick={closePreview} className="text-navy/40 hover:text-navy" data-testid="quote-preview-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 bg-navy/5 overflow-auto flex items-center justify-center">
              {preview.loading ? <Loader2 className="w-6 h-6 animate-spin text-navy/40" />
                : (preview.type || "").startsWith("image/") ? <img src={preview.url} alt={preview.name} className="max-w-full max-h-full object-contain" />
                : ((preview.type || "").includes("pdf") || /\.pdf$/i.test(preview.name)) ? <iframe title={preview.name} src={preview.url} className="w-full h-full" data-testid="quote-preview-frame" />
                : <div className="text-center text-sm text-navy/50 p-8">No se puede previsualizar este tipo de archivo.<br />{preview.url && <a href={preview.url} download={preview.name} className="mt-3 inline-block text-violet underline">Descargar archivo</a>}</div>}
            </div>
          </div>
        </div>
      )}

      {emailOpen && (
        <div className="fixed inset-0 z-[70] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !sending && setEmailOpen(false)} data-testid="quote-email-modal">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-navy/10">
              <Send className="w-4 h-4 text-violet shrink-0" />
              <span className="text-sm font-medium flex-1">Enviar cotización por correo</span>
              <button onClick={() => setEmailOpen(false)} disabled={sending} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              {currentFolio && <div className="text-xs text-navy/50">Folio: <span className="text-violet font-medium">{currentFolio}</span></div>}
              <div>
                <label className="text-xs text-navy/50 block mb-1">Correo del cliente</label>
                <input type="email" className={input} placeholder="cliente@empresa.com" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="quote-email-to" />
              </div>
              <div>
                <label className="text-xs text-navy/50 block mb-1">Mensaje (opcional)</label>
                <textarea rows={3} className={input} placeholder="Estimado cliente, adjuntamos la cotización solicitada…" value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} data-testid="quote-email-message" />
              </div>
              <p className="text-[11px] text-navy/40">Se adjuntará el PDF de la cotización y se guardará este correo en el proyecto.</p>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setEmailOpen(false)} disabled={sending} className="rounded-full border border-navy/15 px-4 py-2 text-sm hover:bg-navy/5 disabled:opacity-50">Cancelar</button>
                <button onClick={sendEmail} disabled={sending} data-testid="quote-email-send" className="rounded-full bg-gradient-violet text-white px-5 py-2 text-sm hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">{sending && <Loader2 className="w-4 h-4 animate-spin" />} Enviar</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {invoiceOpen && (
        <QuoteInvoiceModal source="studio" studio={studio} projectId={id} quoteId={docId}
          quote={{ items, margin_pct: Number(margin), tax_pct: Number(tax), margin_included: marginIncluded, tax_included: taxIncluded }}
          defaultName={p.client} defaultEmail={p.client_email}
          onClose={() => { setInvoiceOpen(false); loadDocs(); }}
          onDone={() => { onSaved(); loadDocs(); }} />
      )}
    </div>
  );
}

/* ---------------- Moodboards ---------------- */
function Moodboards({ studio, id, data, onChange }) {
  const [f, setF] = useState({ title: "", notes: "", images: "" });
  const [uploaded, setUploaded] = useState([]); // imágenes subidas del ordenador (URLs)
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const onFiles = async (ev) => {
    const files = Array.from(ev.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) { urls.push(await uploadImage(file)); }
      setUploaded((prev) => [...prev, ...urls]);
      toast.success(`${urls.length} ${urls.length === 1 ? "imagen subida" : "imágenes subidas"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir la imagen"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const rmUploaded = (u) => setUploaded((prev) => prev.filter((x) => x !== u));
  const add = async () => {
    if (!f.title.trim()) return toast.error("Escribe un título para el moodboard");
    const urlImages = f.images.split(",").map((s) => s.trim()).filter(Boolean);
    const images = [...urlImages, ...uploaded];
    try {
      await api.post(`/studio/${studio}/${id}/moodboards`, { title: f.title.trim(), notes: f.notes, images });
      setF({ title: "", notes: "", images: "" }); setUploaded([]); onChange(); toast.success("Moodboard creado");
    } catch (e) { toast.error("Error"); }
  };
  const del = async (m) => { try { await api.delete(`/studio/${studio}/moodboards/${m.id}`); onChange(); } catch (e) { toast.error("Error"); } };

  // Lightbox (ampliar imagen) + añadir/quitar imágenes de un moodboard existente
  const [lightbox, setLightbox] = useState(null); // { mid, images, index }
  const [addingTo, setAddingTo] = useState(null);
  const openLightbox = (m, idx) => setLightbox({ mid: m.id, images: m.images || [], index: idx });
  const closeLightbox = () => setLightbox(null);
  const lbPrev = () => setLightbox((lb) => ({ ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length }));
  const lbNext = () => setLightbox((lb) => ({ ...lb, index: (lb.index + 1) % lb.images.length }));
  const addImagesTo = async (m, ev) => {
    const files = Array.from(ev.target.files || []);
    if (!files.length) return;
    setAddingTo(m.id);
    try {
      const urls = [];
      for (const file of files) { urls.push(await uploadImage(file)); }
      await api.patch(`/studio/${studio}/moodboards/${m.id}`, { images: [...(m.images || []), ...urls] });
      toast.success(`${urls.length} ${urls.length === 1 ? "imagen añadida" : "imágenes añadidas"}`);
      onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo añadir la imagen"); }
    finally { setAddingTo(null); ev.target.value = ""; }
  };
  const removeImageFrom = async (mid, url) => {
    const m = data.moodboards.find((x) => x.id === mid); if (!m) return;
    try {
      await api.patch(`/studio/${studio}/moodboards/${mid}`, { images: (m.images || []).filter((u) => u !== url) });
      setLightbox((lb) => { if (!lb) return null; const imgs = lb.images.filter((u) => u !== url); return imgs.length ? { ...lb, images: imgs, index: Math.min(lb.index, imgs.length - 1) } : null; });
      onChange();
    } catch { toast.error("No se pudo quitar la imagen"); }
  };

  // Reordenar imágenes (drag & drop) + elegir portada
  const [reorder, setReorder] = useState(null); // { mid, order: [] }
  const [dragIdx, setDragIdx] = useState(null);
  const openReorder = (m) => setReorder({ mid: m.id, order: [...(m.images || [])] });
  const onReorderDrop = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); return; }
    setReorder((r) => { const arr = [...r.order]; const [moved] = arr.splice(dragIdx, 1); arr.splice(i, 0, moved); return { ...r, order: arr }; });
    setDragIdx(null);
  };
  const saveReorder = async () => {
    try { await api.patch(`/studio/${studio}/moodboards/${reorder.mid}`, { images: reorder.order }); toast.success("Orden guardado · portada actualizada"); setReorder(null); onChange(); }
    catch { toast.error("No se pudo guardar el orden"); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-navy/10 rounded-lg p-4 space-y-2">
        <input data-testid="mood-title" placeholder="Título del moodboard" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={input} />
        <input data-testid="mood-images" placeholder="URLs de imágenes separadas por coma (opcional)" value={f.images} onChange={(e) => setF({ ...f, images: e.target.value })} className={input} />
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="mood-file-input" onChange={onFiles} />
          <button type="button" data-testid="mood-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="inline-flex items-center gap-1.5 text-sm rounded-full border border-navy/15 px-4 py-2 hover:border-violet hover:text-violet transition-colors disabled:opacity-50">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Subir imágenes del ordenador
          </button>
          <span className="text-xs text-navy/40">JPG, PNG, WEBP o GIF · hasta 50 MB c/u</span>
        </div>
        {uploaded.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="mood-uploaded-previews">
            {uploaded.map((u, i) => (
              <div key={i} className="relative group/thumb w-16 h-16 rounded-lg overflow-hidden border border-navy/10">
                <img src={mediaUrl(u)} alt="" className="w-full h-full object-cover" />
                <button type="button" data-testid={`mood-uploaded-rm-${i}`} onClick={() => rmUploaded(u)}
                  className="absolute top-0.5 right-0.5 bg-navy/70 text-white rounded-full p-0.5 opacity-0 group-hover/thumb:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
        <textarea placeholder="Notas" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={input} />
        <button data-testid="mood-add" onClick={add} className="rounded-full bg-gradient-violet text-white px-5 py-2 text-sm hover:opacity-90 transition-opacity">Crear moodboard</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.moodboards.map((m) => {
          const imgs = m.images || [];
          return (
            <div key={m.id} className="bg-white border border-navy/10 rounded-lg overflow-hidden group" data-testid={`mood-${m.id}`}>
              {imgs.length > 0 ? (
                <div className="grid grid-cols-2 gap-px bg-navy/5">
                  {imgs.slice(0, 4).map((im, i) => {
                    const isLast = i === 3 && imgs.length > 4;
                    return (
                      <button key={i} type="button" onClick={() => openLightbox(m, i)} data-testid={`mood-img-${m.id}-${i}`}
                        className="relative aspect-square overflow-hidden group/img">
                        <img src={mediaUrl(im)} alt="" className="w-full h-full object-cover transition-transform group-hover/img:scale-105" />
                        {isLast
                          ? <div className="absolute inset-0 bg-navy/60 flex items-center justify-center text-white text-lg font-medium">+{imgs.length - 4}</div>
                          : <div className="absolute inset-0 group-hover/img:bg-navy/25 transition-colors flex items-center justify-center"><Maximize2 className="w-5 h-5 text-white opacity-0 group-hover/img:opacity-100 transition-opacity" /></div>}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="aspect-[2/1] bg-navy/5 flex items-center justify-center text-navy/30 text-xs">Sin imágenes</div>
              )}
              <div className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div><div className="text-sm font-medium">{m.title}</div>{m.notes && <div className="text-xs text-navy/50 mt-0.5">{m.notes}</div>}</div>
                  <button onClick={() => del(m)} className="text-navy/20 hover:text-destructive opacity-0 group-hover:opacity-100 transition-all shrink-0"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label data-testid={`mood-add-images-${m.id}`} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-violet hover:text-violet transition-colors cursor-pointer">
                    {addingTo === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Añadir imágenes
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(ev) => addImagesTo(m, ev)} />
                  </label>
                  {imgs.length >= 2 && (
                    <button type="button" data-testid={`mood-reorder-${m.id}`} onClick={() => openReorder(m)} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-violet hover:text-violet transition-colors"><Layers className="w-3.5 h-3.5" /> Ordenar</button>
                  )}
                  <ShareButton studio={studio} id={id} kind="moodboard" refId={m.id} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {lightbox && lightbox.images.length > 0 && (
        <div className="fixed inset-0 z-[80] bg-navy/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeLightbox} data-testid="mood-lightbox">
          <div className="absolute top-4 left-4 text-white/60 text-sm">{lightbox.index + 1} / {lightbox.images.length}</div>
          <button onClick={closeLightbox} data-testid="mood-lightbox-close" className="absolute top-4 right-4 text-white/70 hover:text-white"><X className="w-7 h-7" /></button>
          {lightbox.images.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); lbPrev(); }} data-testid="mood-lightbox-prev" className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"><ChevronLeft className="w-6 h-6" /></button>
          )}
          <img src={mediaUrl(lightbox.images[lightbox.index])} alt="" onClick={(e) => e.stopPropagation()} data-testid="mood-lightbox-img" className="max-w-[90vw] max-h-[82vh] object-contain rounded-lg shadow-2xl" />
          {lightbox.images.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); lbNext(); }} data-testid="mood-lightbox-next" className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"><ChevronRight className="w-6 h-6" /></button>
          )}
          <button onClick={(e) => { e.stopPropagation(); removeImageFrom(lightbox.mid, lightbox.images[lightbox.index]); }} data-testid="mood-lightbox-remove" className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white bg-white/10 hover:bg-destructive/80 rounded-full px-4 py-2 transition-colors"><Trash2 className="w-4 h-4" /> Quitar esta imagen</button>
        </div>
      )}

      {reorder && (
        <div className="fixed inset-0 z-[80] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setReorder(null)} data-testid="mood-reorder-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-navy/10">
              <Layers className="w-4 h-4 text-violet shrink-0" />
              <span className="text-sm font-medium flex-1">Ordenar imágenes · arrastra para reordenar</span>
              <button onClick={() => setReorder(null)} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 overflow-auto">
              <p className="text-xs text-navy/50 mb-3">La primera imagen será la <b>portada</b> del moodboard.</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="mood-reorder-grid">
                {reorder.order.map((u, i) => (
                  <div key={u} draggable data-testid={`mood-reorder-item-${i}`}
                    onDragStart={() => setDragIdx(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onReorderDrop(i)}
                    className={`relative aspect-square rounded-lg overflow-hidden border cursor-move transition-all ${dragIdx === i ? "opacity-40 border-violet" : "border-navy/10 hover:border-violet/50"}`}>
                    <img src={mediaUrl(u)} alt="" className="w-full h-full object-cover pointer-events-none" />
                    {i === 0 && <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet text-white">Portada</span>}
                    <span className="absolute bottom-1 right-1 text-[10px] w-5 h-5 flex items-center justify-center rounded-full bg-navy/70 text-white">{i + 1}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-navy/10">
              <button onClick={() => setReorder(null)} className="rounded-full border border-navy/15 px-4 py-2 text-sm hover:bg-navy/5">Cancelar</button>
              <button onClick={saveReorder} data-testid="mood-reorder-save" className="rounded-full bg-gradient-violet text-white px-5 py-2 text-sm hover:opacity-90">Guardar orden</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Renders (AI) ---------------- */
function Renders({ studio, id, p, onChange }) {
  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState("gemini");
  const [busy, setBusy] = useState(false);
  const [redBusy, setRedBusy] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [refFile, setRefFile] = useState(null);
  const refInput = useRef();
  const [uploadingOwn, setUploadingOwn] = useState(false);
  const ownRef = useRef();
  const uploadOwn = async (ev) => {
    const files = Array.from(ev.target.files || []); if (!files.length) return;
    setUploadingOwn(true);
    try {
      const urls = [];
      for (const file of files) { urls.push(await uploadImage(file)); }
      await api.post(`/studio/${studio}/${id}/renders`, { urls });
      toast.success(`${urls.length} ${urls.length === 1 ? "imagen subida" : "imágenes subidas"}`); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir la imagen"); }
    finally { setUploadingOwn(false); if (ownRef.current) ownRef.current.value = ""; }
  };
  const removeRender = async (url) => {
    try { await api.delete(`/studio/${studio}/${id}/renders`, { data: { url } }); onChange(); }
    catch { toast.error("No se pudo quitar la imagen"); }
  };
  const gen = async () => {
    if (!prompt.trim()) return; setBusy(true);
    try { await api.post(`/studio/${studio}/${id}/ai/image`, { prompt, provider }); setPrompt(""); toast.success("Imagen generada"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };
  const redecorate = async () => {
    if (!refFile) { toast.error("Sube una foto primero"); return; }
    if (!instruction.trim()) { toast.error("Escribe qué cambios quieres"); return; }
    setRedBusy(true);
    const fd = new FormData(); fd.append("file", refFile); fd.append("instruction", instruction);
    try { await api.post(`/studio/${studio}/${id}/ai/redecorate`, fd); setInstruction(""); setRefFile(null); if (refInput.current) refInput.current.value = ""; toast.success("Espacio redecorado"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setRedBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-navy/10 rounded-lg p-4 space-y-2">
          <div className="text-sm font-medium flex items-center gap-2"><Wand2 className="w-4 h-4 text-violet" /> Generar desde texto</div>
          <textarea data-testid="render-prompt" rows={2} placeholder="Describe la imagen que quieres generar…" value={prompt} onChange={(e) => setPrompt(e.target.value)} className={input} />
          <div className="flex items-center gap-2">
            <select value={provider} onChange={(e) => setProvider(e.target.value)} className={`${input} w-40`}>
              <option value="gemini">Gemini (Nano Banana)</option>
              <option value="openai">OpenAI (GPT Image)</option>
            </select>
            <button data-testid="render-generate" onClick={gen} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white px-5 py-2.5 text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Generar
            </button>
          </div>
        </div>
        <div className="bg-white border border-navy/10 rounded-lg p-4 space-y-2">
          <div className="text-sm font-medium flex items-center gap-2"><ImageIcon className="w-4 h-4 text-violet" /> Redecorar sobre una foto</div>
          <input ref={refInput} type="file" accept="image/*" data-testid="render-ref-file" onChange={(e) => setRefFile(e.target.files?.[0] || null)} className="hidden" />
          <button onClick={() => refInput.current?.click()} className="w-full text-sm text-navy/60 hover:text-violet border border-dashed border-navy/20 rounded-lg py-3 transition-colors">
            {refFile ? refFile.name.slice(0, 28) : "Subir foto del espacio real"}
          </button>
          <textarea data-testid="render-instruction" rows={2} placeholder="¿Qué cambios quieres? Ej: estilo minimalista cálido, tonos tierra…" value={instruction} onChange={(e) => setInstruction(e.target.value)} className={input} />
          <button data-testid="render-redecorate" onClick={redecorate} disabled={redBusy} className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-5 py-2.5 text-sm hover:bg-navy-soft transition-colors disabled:opacity-50">
            {redBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Redecorar
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={ownRef} type="file" accept="image/*" multiple className="hidden" data-testid="render-upload-input" onChange={uploadOwn} />
        <button data-testid="render-upload" onClick={() => ownRef.current?.click()} disabled={uploadingOwn}
          className="inline-flex items-center gap-1.5 text-sm rounded-full border border-navy/15 px-4 py-2 hover:border-violet hover:text-violet transition-colors disabled:opacity-50">
          {uploadingOwn ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Subir imágenes propias
        </button>
        <span className="text-xs text-navy/40">Mézclalas con las generadas por IA</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="render-gallery">
        {(p.renders || []).slice().reverse().map((r, i) => (
          <div key={i} className="relative group/r rounded-lg overflow-hidden border border-navy/10 hover:border-violet/40 transition-colors">
            <a href={mediaUrl(r)} target="_blank" rel="noreferrer" className="block"><img src={mediaUrl(r)} alt="" className="w-full aspect-[4/3] object-cover" /></a>
            <button data-testid={`render-remove-${i}`} onClick={() => removeRender(r)} className="absolute top-1.5 right-1.5 bg-navy/70 hover:bg-destructive text-white rounded-full p-1 opacity-0 group-hover/r:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {(p.renders || []).length === 0 && <div className="text-navy/40 text-sm col-span-full py-6 text-center">Aún no hay renders. Genera el primero o sube los tuyos.</div>}
      </div>
    </div>
  );
}

/* ---------------- Finance ---------------- */
function Finance({ studio, id, data, onChange }) {
  const [f, setF] = useState({ type: "invoice", concept: "", amount: "", supplier: "", date: "", paid_date: "", expected_invoice_date: "", expected_payment_date: "" });
  const [imp, setImp] = useState(false);
  const [editId, setEditId] = useState(null);
  const [fiscalizing, setFiscalizing] = useState(null);
  const [timbrar, setTimbrar] = useState(null);
  const impRef = useRef();
  const uploadFiscal = async (e, ev) => {
    const list = Array.from(ev.target.files || []); ev.target.value = ""; if (!list.length) return;
    setFiscalizing(e.id);
    const fd = new FormData(); list.forEach((file) => fd.append("files", file));
    try {
      const { data: r } = await api.post(`/studio/${studio}/finance/entries/${e.id}/fiscal`, fd);
      toast.success("Factura fiscal adjuntada" + (r?.synced_to_accounting ? " · enviada a Contabilidad" : ""));
      onChange();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "No se pudo subir la factura fiscal"); }
    finally { setFiscalizing(null); }
  };
  const TYPES = [["invoice", "Ingreso / Factura"], ["expense", "Gasto"], ["supplier_payment", "Pago a proveedor"], ["supplier_quote", "Cotización de proveedor"], ["quote", "Cotización (cliente)"]];
  const patchEntry = async (eid, patch) => { try { await api.patch(`/studio/${studio}/finance/entries/${eid}`, patch); onChange(); } catch (er) { toast.error("Error"); } };
  const add = async () => { if (!f.concept.trim()) return; try { await api.post(`/studio/${studio}/${id}/finance/entries`, { ...f, amount: Number(f.amount || 0) }); setF({ type: "invoice", concept: "", amount: "", supplier: "", date: "", paid_date: "", expected_invoice_date: "", expected_payment_date: "" }); onChange(); } catch (e) { toast.error("Error"); } };
  const del = async (e) => { try { await api.delete(`/studio/${studio}/finance/entries/${e.id}`); onChange(); } catch (er) { toast.error("Error"); } };
  const togglePaid = async (e) => { try { await api.patch(`/studio/${studio}/finance/entries/${e.id}`, { status: e.status === "paid" ? "pending" : "paid", ...(e.status === "paid" ? {} : (e.paid_date ? {} : { paid_date: new Date().toISOString().slice(0, 10) })) }); onChange(); } catch (er) { toast.error("Error"); } };
  const importInvoice = async (ev) => {
    const list = Array.from(ev.target.files || []); if (!list.length) return; setImp(true);
    const fd = new FormData();
    list.forEach((file) => fd.append("files", file));
    fd.append("type", f.type);
    fd.append("expected_payment_date", f.expected_payment_date || "");
    fd.append("expected_invoice_date", f.expected_invoice_date || "");
    fd.append("paid_date", f.paid_date || "");
    try {
      const { data: resp } = await api.post(`/studio/${studio}/${id}/finance/import`, fd);
      const isQ = f.type === "supplier_quote" || f.type === "quote";
      const kinds = list.map((x) => (x.name.split(".").pop() || "").toUpperCase());
      toast.success((isQ ? "Cotización importada" : "Factura importada") + (kinds.length > 1 ? ` (${kinds.join(" + ")})` : "") + (resp?.synced_to_accounting ? " · enviada a Finanzas" : "") + (f.expected_payment_date ? ` · pago programado ${f.expected_payment_date}` : ""));
      setF({ type: "invoice", concept: "", amount: "", supplier: "", date: "", paid_date: "", expected_invoice_date: "", expected_payment_date: "" });
      onChange();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo leer el documento"); } finally { setImp(false); if (impRef.current) impRef.current.value = ""; }
  };
  const fin = data.finance;
  const isQuoteType = f.type === "supplier_quote" || f.type === "quote";
  const convertToExpense = async (e) => {
    if (e.converted) { toast.info("Esta cotización ya se convirtió en gasto"); return; }
    try { await api.post(`/studio/${studio}/finance/entries/${e.id}/convert`); toast.success("Convertida a gasto · la cotización se conserva en el expediente"); onChange(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "No se pudo convertir"); }
  };
  const supplierQuotes = (data.entries || []).filter((e) => e.type === "supplier_quote");
  const minQuote = supplierQuotes.length ? Math.min(...supplierQuotes.map((e) => Number(e.amount || 0))) : 0;
  return (
    <div className="space-y-4">
      {timbrar && <TimbrarCFDIModal source="studio" studio={studio} entryId={timbrar.id} amount={timbrar.amount} defaultName={timbrar.client || ""} onClose={() => setTimbrar(null)} onDone={onChange} />}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[["Valor del proyecto", fin.value ?? 0, "text-navy"], ["Facturado", fin.income, "text-emerald-600"], ["Gastos", fin.expenses, "text-rose-600"], ["Utilidad", fin.profit, fin.profit >= 0 ? "text-emerald-600" : "text-rose-600"], ["ROI", `${fin.roi}%`, "text-violet"]].map(([l, v, c]) => (
          <div key={l} className="bg-white border border-navy/10 rounded-lg p-4" data-testid={`fin-card-${l}`}><div className="text-xs text-navy/40">{l}</div><div className={`text-xl font-medium ${c}`}>{typeof v === "number" ? money(v) : v}</div></div>
        ))}
      </div>
      {(fin.po_expenses > 0 || (fin.value ?? 0) > 0) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-navy/55 px-1" data-testid="fin-value-breakdown">
          {(fin.value ?? 0) > 0 && <span><DollarSign className="inline w-3.5 h-3.5 text-navy/40" /> Valor por cotizaciones aprobadas: <span className="font-medium text-navy/80">{money(fin.value)}</span></span>}
          {fin.po_expenses > 0 && <span>· Incluye órdenes de compra aprobadas: <span className="font-medium text-rose-600">{money(fin.po_expenses)}</span></span>}
        </div>
      )}
      <div className="bg-white border border-navy/10 rounded-lg p-4 grid md:grid-cols-6 gap-2 items-end">
        <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={input}>{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <input data-testid="fin-concept" placeholder="Concepto" value={f.concept} onChange={(e) => setF({ ...f, concept: e.target.value })} className={`${input} md:col-span-2`} />
        <input data-testid="fin-amount" type="number" placeholder="Monto" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={input} />
        <button data-testid="fin-add" onClick={add} className="rounded-full bg-gradient-violet text-white text-sm py-2.5 hover:opacity-90 transition-opacity">Añadir</button>
        <input ref={impRef} type="file" accept=".pdf,.xml" multiple className="hidden" data-testid="fin-import-input" onChange={importInvoice} />
        <button data-testid="fin-import" onClick={() => impRef.current?.click()} disabled={imp} className="inline-flex items-center justify-center gap-1.5 rounded-full border border-navy/15 text-sm py-2.5 hover:border-violet hover:text-violet transition-colors disabled:opacity-50">
          {imp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {isQuoteType ? "Subir cotización (PDF)" : "Subir factura (PDF y/o XML)"}
        </button>
      </div>
      <div className="bg-white border border-navy/10 rounded-lg p-4" data-testid="fin-dates">
        <div className="text-[11px] font-medium text-navy/60 mb-2 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5 text-violet" /> Fechas para el registro que agregues o subas arriba (opcional · edita las de un registro existente con el ícono de reloj de su fila)</div>
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <label className="text-[11px] text-navy/50">Fecha de factura
            <input data-testid="fin-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={`${input} mt-1`} /></label>
          <label className="text-[11px] text-navy/50">Fecha de cobro (pagado)
            <input data-testid="fin-paid-date" type="date" value={f.paid_date} onChange={(e) => setF({ ...f, paid_date: e.target.value })} className={`${input} mt-1`} /></label>
          <label className="text-[11px] text-navy/50">Fecha estimada de facturación
            <input data-testid="fin-exp-invoice" type="date" value={f.expected_invoice_date} onChange={(e) => setF({ ...f, expected_invoice_date: e.target.value })} className={`${input} mt-1`} /></label>
          <label className="text-[11px] text-navy/50">Fecha estimada de cobro
            <input data-testid="fin-exp-payment" type="date" value={f.expected_payment_date} onChange={(e) => setF({ ...f, expected_payment_date: e.target.value })} className={`${input} mt-1`} /></label>
        </div>
      </div>
      {fin.supplier_quotes > 0 && (
        <div className="flex items-center gap-2 text-xs text-navy/55 px-1" data-testid="fin-supplier-quotes-total">
          <DollarSign className="w-3.5 h-3.5 text-navy/40" />
          Cotizaciones de proveedores (estimado): <span className="font-medium text-navy/80">{money(fin.supplier_quotes)}</span>
        </div>
      )}
      {supplierQuotes.length >= 2 && (
        <div className="bg-white border border-navy/10 rounded-lg p-4" data-testid="fin-supplier-compare">
          <div className="flex items-center gap-2 mb-3">
            <GitCompare className="w-4 h-4 text-violet" />
            <span className="text-sm font-medium">Comparar proveedores</span>
            <span className="text-[11px] text-navy/40">· {supplierQuotes.length} cotizaciones · la más baja marcada</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...supplierQuotes].sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0)).map((e) => {
              const best = Number(e.amount || 0) === minQuote;
              return (
                <div key={e.id} data-testid={`fin-compare-${e.id}`}
                  className={`rounded-lg border p-3 flex flex-col gap-1.5 ${best ? "border-emerald-500 bg-emerald-500/[0.04]" : "border-navy/10"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{e.supplier || "Proveedor"}</span>
                    {best && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 shrink-0">Más conveniente</span>}
                  </div>
                  <div className="text-xs text-navy/50 truncate">{e.concept || "—"}</div>
                  <div className="text-lg font-medium mt-auto">{money(e.amount)}</div>
                  <button data-testid={`fin-compare-convert-${e.id}`} onClick={() => convertToExpense(e)}
                    className="mt-1 inline-flex items-center justify-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-emerald-500 hover:text-emerald-600 transition-colors">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Elegir y convertir a gasto
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="space-y-2">
        {data.entries.map((e) => (
          <div key={e.id} className="bg-white border border-navy/10 rounded-lg group" data-testid={`fin-entry-${e.id}`}>
            <div className="flex items-center gap-3 px-4 py-2.5">
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-navy/10 text-navy/60">{(TYPES.find((t) => t[0] === e.type) || [])[1]}</span>
              <span className="text-sm flex-1 truncate">{e.concept}{e.supplier ? ` · ${e.supplier}` : ""}</span>
              {(e.expected_payment_date || e.paid_date) && (
                <span className="text-[11px] text-navy/45 whitespace-nowrap">
                  {e.paid_date ? `Pagada: ${fmtDate(e.paid_date)}` : `Prog. pago: ${fmtDate(e.expected_payment_date)}`}
                </span>
              )}
              {(e.attachments || []).map((a) => (
                <button key={a.id} data-testid={`fin-att-${a.id}`} onClick={() => downloadBlob(`/studio/${studio}/finance/entries/${e.id}/attachment/${a.id}`, a.filename)} className="text-navy/40 hover:text-violet"><Download className="w-4 h-4" /></button>
              ))}
              {e.type === "supplier_quote" ? (
                <button data-testid={`fin-convert-${e.id}`} onClick={() => convertToExpense(e)}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 transition-colors inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Convertir a gasto
                </button>
              ) : (
                <>
                  {e.type === "invoice" && (
                    (e.fiscal || (e.attachments && e.attachments.length > 0)) ? (
                      <span data-testid={`fin-fiscal-badge-${e.id}`} className="text-[11px] px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> Facturada ✓</span>
                    ) : (
                      <>
                        <button data-testid={`fin-timbrar-${e.id}`} onClick={() => setTimbrar(e)} title="Timbrar CFDI real ante el SAT" className="text-[11px] px-2.5 py-1 rounded-full bg-gradient-violet text-white hover:opacity-90 transition-opacity inline-flex items-center gap-1"><FileCheck2 className="w-3.5 h-3.5" /> Timbrar CFDI</button>
                        <label data-testid={`fin-fiscal-btn-${e.id}`} title="Subir el PDF/XML fiscal real y reemplazar" className="text-[11px] px-2.5 py-1 rounded-full bg-violet/10 text-violet hover:bg-violet/20 transition-colors inline-flex items-center gap-1 cursor-pointer">
                          {fiscalizing === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} Subir PDF/XML
                          <input type="file" accept=".pdf,.xml" multiple className="hidden" onChange={(ev) => uploadFiscal(e, ev)} />
                        </label>
                      </>
                    )
                  )}
                  <button data-testid={`fin-edit-dates-${e.id}`} title="Editar fechas de este registro" onClick={() => setEditId(editId === e.id ? null : e.id)}
                    className={`text-[11px] px-2.5 py-1 rounded-full inline-flex items-center gap-1 transition-colors ${editId === e.id ? "bg-violet/10 text-violet" : "bg-navy/5 text-navy/50 hover:text-violet"}`}><CalendarClock className="w-3.5 h-3.5" /> Fechas</button>
                  <button data-testid={`fin-paid-${e.id}`} onClick={() => togglePaid(e)}
                    className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${e.status === "paid" ? "bg-emerald-500/10 text-emerald-600" : "bg-gold/15 text-gold hover:bg-gold/25"}`}>
                    {e.status === "paid" ? "Pagada" : "Por pagar"}
                  </button>
                </>
              )}
              <span className="text-sm font-medium w-24 text-right">{money(e.amount)}</span>
              <button onClick={() => del(e)} className="text-navy/20 hover:text-destructive opacity-0 group-hover:opacity-100 transition-all"><Trash2 className="w-4 h-4" /></button>
            </div>
            {editId === e.id && (
              <div className="border-t border-navy/10 px-4 py-3 grid md:grid-cols-3 gap-3 bg-navy/[0.02]" data-testid={`fin-edit-panel-${e.id}`}>
                <label className="text-[11px] text-navy/50">Fecha estimada de facturación
                  <input data-testid={`fin-edit-exp-invoice-${e.id}`} type="date" defaultValue={(e.expected_invoice_date || "").slice(0, 10)} onChange={(ev) => patchEntry(e.id, { expected_invoice_date: ev.target.value })} className={`${input} mt-1`} /></label>
                <label className="text-[11px] text-navy/50">Fecha programada de pago
                  <input data-testid={`fin-edit-exp-payment-${e.id}`} type="date" defaultValue={(e.expected_payment_date || "").slice(0, 10)} onChange={(ev) => patchEntry(e.id, { expected_payment_date: ev.target.value })} className={`${input} mt-1`} /></label>
                <label className="text-[11px] text-navy/50">Fecha en que se pagó
                  <input data-testid={`fin-edit-paid-${e.id}`} type="date" defaultValue={(e.paid_date || "").slice(0, 10)} onChange={(ev) => patchEntry(e.id, { paid_date: ev.target.value, status: "paid" })} className={`${input} mt-1`} /></label>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Metrics ---------------- */
function Metrics({ studio, id, p, data, onChange }) {
  const m = p.metrics || {};
  const [f, setF] = useState({ reach: m.reach || "", impressions: m.impressions || "", clicks: m.clicks || "", conversions: m.conversions || "", spend: m.spend || "", revenue: m.revenue || "" });
  const F = [["reach", "Alcance"], ["impressions", "Impresiones"], ["clicks", "Clics"], ["conversions", "Conversiones"], ["spend", "Inversión"], ["revenue", "Ingresos"]];
  const save = async () => { try { const body = {}; Object.keys(f).forEach((k) => { if (f[k] !== "") body[k] = Number(f[k]); }); await api.patch(`/studio/${studio}/${id}/metrics`, body); toast.success("Métricas guardadas"); onChange(); } catch (e) { toast.error("Error"); } };
  const fin = data.finance;
  return (
    <div className="space-y-4">
      <div className="bg-white border border-navy/10 rounded-lg p-6" data-testid="metrics-chart">
        <div className="overline text-navy/40 mb-4">Gastos vs Valor del proyecto</div>
        <div style={{ width: "100%", height: 200 }}>
          <ResponsiveContainer>
            <BarChart data={[{ name: "Gastos", value: Number(fin.expenses || 0) }, { name: "Valor", value: Number(fin.value ?? fin.income ?? 0) }]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0A142810" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#0A142880" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#0A142860" }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
              <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#0A142808" }} contentStyle={{ borderRadius: 12, border: "1px solid #0A142815", fontSize: 12 }} />
              <Bar dataKey="value" radius={[8, 8, 0, 0]} maxBarSize={90}><Cell fill="#DB2777" /><Cell fill="#059669" /></Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap gap-4 mt-2 text-xs text-navy/60"><span>ROAS {p.roas ?? 0}x</span><span>CPC {money(p.cpc)}</span><span>CVR {p.cvr ?? 0}%</span></div>
      </div>
      <div className="bg-white border border-navy/10 rounded-lg p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        {F.map(([k, l]) => (
          <div key={k}><label className="text-xs text-navy/50 block mb-1">{l}</label><input data-testid={`metric-${k}`} type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={input} /></div>
        ))}
        <div className="col-span-full"><button data-testid="metric-save" onClick={save} className="rounded-full bg-gradient-violet text-white px-6 py-2.5 text-sm hover:opacity-90 transition-opacity">Guardar métricas</button></div>
      </div>
    </div>
  );
}

/* ---------------- Files ---------------- */
function Files({ studio, id }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const load = useCallback(() => { api.get(`/studio/${studio}/${id}/files`).then((r) => setFiles(r.data)).catch(() => {}); }, [studio, id]);
  useEffect(() => { load(); }, [load]);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/studio/${studio}/${id}/files`, fd); toast.success("Archivo subido"); load(); } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || "Error"); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const del = async (f) => { try { await api.delete(`/studio/${studio}/files/${f.id}`); load(); } catch (e) { toast.error("Error"); } };
  return (
    <div className="space-y-4">
      <input ref={ref} type="file" onChange={upload} className="hidden" data-testid="files-input" />
      <button data-testid="files-upload" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white px-5 py-2.5 text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Subir archivo
      </button>
      <div className="space-y-2">
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-3 bg-white border border-navy/10 rounded-lg px-4 py-3 group" data-testid={`file-${f.id}`}>
            <FileText className="w-4 h-4 text-violet shrink-0" />
            <div className="flex-1 min-w-0"><div className="text-sm truncate">{f.filename}</div><div className="text-[11px] text-navy/40">{(f.size / 1024).toFixed(0)} KB · {f.uploader_name}</div></div>
            <button data-testid={`file-download-${f.id}`} onClick={() => downloadBlob(`/studio/${studio}/files/${f.id}/download`, f.filename)} className="text-navy/40 hover:text-violet"><Download className="w-4 h-4" /></button>
            <button onClick={() => del(f)} className="text-navy/20 hover:text-destructive opacity-0 group-hover:opacity-100 transition-all"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {files.length === 0 && <div className="text-navy/40 text-sm py-6 text-center">Sin archivos.</div>}
      </div>
    </div>
  );
}

/* ---------------- Comments (with attachments) ---------------- */
function Comments({ studio, id }) {
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const load = useCallback(() => { api.get(`/studio/${studio}/${id}/comments`).then((r) => setList(r.data)).catch(() => {}); }, [studio, id]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!text.trim() && !file) return; setBusy(true);
    try {
      if (file) { const fd = new FormData(); fd.append("file", file); fd.append("body", text); await api.post(`/studio/${studio}/${id}/comments/upload`, fd); }
      else { await api.post(`/studio/${studio}/${id}/comments`, { body: text }); }
      setText(""); setFile(null); if (ref.current) ref.current.value = ""; load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {list.map((c) => (
          <div key={c.id} className="bg-white border border-navy/10 rounded-lg p-4" data-testid={`comment-${c.id}`}>
            <div className="flex items-center gap-2 mb-1"><span className="w-6 h-6 rounded-full bg-violet/15 text-violet text-[11px] flex items-center justify-center font-medium">{(c.author_name || "?").slice(0, 1).toUpperCase()}</span><span className="text-sm font-medium">{c.author_name}</span><span className="text-[11px] text-navy/35">{(c.created_at || "").slice(0, 10)}</span></div>
            {c.body && <p className="text-sm text-navy/75 whitespace-pre-wrap">{c.body}</p>}
            {(c.attachments || []).map((a) => (
              <button key={a.id} data-testid={`comment-att-${a.id}`} onClick={() => downloadBlob(`/studio/${studio}/comments/${c.id}/attachment/${a.id}`, a.filename)}
                className="mt-2 inline-flex items-center gap-2 text-sm text-violet bg-violet/5 rounded-lg px-3 py-2 hover:bg-violet/10 transition-colors">
                <Paperclip className="w-3.5 h-3.5" /> {a.filename} <Download className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
        ))}
        {list.length === 0 && <div className="text-navy/40 text-sm py-6 text-center">Sé el primero en comentar.</div>}
      </div>
      <div className="bg-white border border-navy/10 rounded-lg p-3 space-y-2">
        <textarea data-testid="comment-input" rows={2} placeholder="Escribe un mensaje…" value={text} onChange={(e) => setText(e.target.value)} className={input} />
        <div className="flex items-center gap-2">
          <input ref={ref} type="file" data-testid="comment-file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" />
          <button onClick={() => ref.current?.click()} className="inline-flex items-center gap-1.5 text-sm text-navy/60 hover:text-violet border border-navy/15 rounded-full px-3 py-2 transition-colors"><Paperclip className="w-4 h-4" /> {file ? file.name.slice(0, 18) : "Adjuntar"}</button>
          <button data-testid="comment-send" onClick={send} disabled={busy} className="ml-auto inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white px-5 py-2 text-sm hover:opacity-90 transition-opacity disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Invite dialog ---------------- */
function InviteDialog({ studio, id, members, onClose, onChange }) {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get(`/studio/${studio}/users`).then((r) => setUsers(r.data)).catch(() => {}); }, [studio]);
  const memberIds = new Set((members || []).map((m) => m.id));
  const add = async (u) => { try { await api.post(`/studio/${studio}/${id}/members`, { user_id: u.id }); toast.success(`${u.name} agregado`); onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } };
  const rm = async (m) => { try { await api.delete(`/studio/${studio}/${id}/members/${m.id}`); onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }} onClick={(e) => e.stopPropagation()} data-testid="invite-modal" className="w-full max-w-md bg-cream rounded-xl p-8 max-h-[85vh] overflow-y-auto thin-scroll">
        <div className="flex items-center justify-between mb-5"><h3 className="text-xl font-medium flex items-center gap-2"><Users className="w-5 h-5 text-violet" /> Miembros del proyecto</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {(members || []).length > 0 && <div className="mb-5 space-y-2">{members.map((m) => (
          <div key={m.id} className="flex items-center justify-between bg-white border border-navy/10 rounded-lg px-4 py-2.5">
            <span className="text-sm flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> {m.name} <span className="text-navy/40 text-xs">{m.email}</span></span>
            <button data-testid={`member-remove-${m.id}`} onClick={() => rm(m)} className="text-xs text-navy/40 hover:text-destructive">Quitar</button>
          </div>
        ))}</div>}
        <div className="overline text-navy/40 mb-2">Agregar personas</div>
        <div className="space-y-2">
          {users.filter((u) => !memberIds.has(u.id)).map((u) => (
            <button key={u.id} data-testid={`invite-user-${u.id}`} onClick={() => add(u)} className="w-full flex items-center justify-between bg-white border border-navy/10 rounded-lg px-4 py-2.5 hover:border-violet/40 transition-colors text-left">
              <span className="min-w-0"><span className="block text-sm font-medium truncate">{u.name}</span><span className="block text-xs text-navy/45 truncate">{u.email}</span></span>
              <UserPlus className="w-4 h-4 text-violet shrink-0" />
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}


function ConceptBoard({ studio, id, projectName }) {
  const [board, setBoard] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const patchImages = async (images) => {
    const { data } = await api.patch(`/studio/${studio}/${id}/concept-board/images`, { images });
    return data;
  };
  const uploadOwnImages = async (ev) => {
    const files = Array.from(ev.target.files || []); if (!files.length) return;
    setUploadingImg(true);
    try {
      const urls = [];
      for (const file of files) { urls.push(await uploadImage(file)); }
      await patchImages([...(board?.images || []), ...urls]);
      toast.success(`${urls.length} ${urls.length === 1 ? "imagen subida" : "imágenes subidas"}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir la imagen"); }
    finally { setUploadingImg(false); ev.target.value = ""; }
  };
  const removeImg = async (url) => {
    try { await patchImages((board?.images || []).filter((u) => u !== url)); load(); }
    catch { toast.error("No se pudo quitar la imagen"); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const { exportConceptBoardPdf } = await import("../../lib/conceptPdf");
      await exportConceptBoardPdf(board, projectName || "Proyecto");
      toast.success("Concept Board exportado a PDF");
    } catch (e) { toast.error("No se pudo exportar el PDF"); }
    finally { setExporting(false); }
  };

  const load = useCallback(() => {
    api.get(`/studio/${studio}/${id}/concept-board`).then((r) => { setBoard(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  }, [studio, id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (board?.status === "generating") { const t = setInterval(load, 4000); return () => clearInterval(t); }
  }, [board?.status, load]);

  const generate = async () => {
    setBusy(true);
    try { await api.post(`/studio/${studio}/${id}/concept-board`, { prompt }); toast.success("Generando Concept Board…"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };

  if (!loaded) return <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-violet" /></div>;
  const generating = board?.status === "generating";
  const ready = board?.status === "ready";

  return (
    <div className="space-y-5" data-testid="conceptboard-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-medium text-lg">Concept Board</h3>
          <p className="text-sm text-navy/50">Dirección creativa generada con IA a partir del brief del proyecto.</p>
        </div>
        <div className="flex items-center gap-2">
          {board && (
            <ShareButton studio={studio} id={id} kind="concept" refId={id}
              className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-violet hover:text-violet transition-colors disabled:opacity-50" />
          )}
          {ready && !generating && (
            <button data-testid="conceptboard-export-pdf" onClick={exportPdf} disabled={exporting} className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-4 py-2.5 hover:opacity-90 disabled:opacity-50">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Exportar PDF
            </button>
          )}
          {(ready || board?.status === "error") && !generating && (
            <button data-testid="conceptboard-regen" onClick={generate} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:bg-navy/5 disabled:opacity-50"><RefreshCw className="w-4 h-4" /> Regenerar</button>
          )}
        </div>
      </div>

      {loaded && (
        <div className="bg-white border border-navy/10 rounded-lg p-4 space-y-3" data-testid="conceptboard-own">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="overline text-navy/40">Imágenes del concept{(board?.images?.length || 0) > 0 ? ` (${board.images.length})` : ""}</div>
            <label data-testid="conceptboard-upload" className="inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-violet hover:text-violet transition-colors cursor-pointer">
              {uploadingImg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />} Subir imágenes propias
              <input type="file" accept="image/*" multiple className="hidden" onChange={uploadOwnImages} />
            </label>
          </div>
          {(board?.images?.length || 0) > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="conceptboard-images">
              {board.images.map((u, i) => (
                <div key={i} className="relative group/c rounded-lg overflow-hidden border border-navy/10">
                  <a href={mediaUrl(u)} target="_blank" rel="noreferrer" className="block"><img src={mediaUrl(u)} alt={`concepto ${i + 1}`} className="w-full aspect-square object-cover" /></a>
                  <button data-testid={`conceptboard-img-remove-${i}`} onClick={() => removeImg(u)} className="absolute top-1.5 right-1.5 bg-navy/70 hover:bg-destructive text-white rounded-full p-1 opacity-0 group-hover/c:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-navy/40">Sube tus propias fotos para mezclarlas con las que genere la IA.</p>
          )}
        </div>
      )}

      {(!board || (!ready && !generating)) ? (
        <div className="bg-white border border-navy/10 rounded-lg p-6 space-y-3">
          {board?.status === "error" && <p className="text-sm text-rose-600">Falló la generación. Intenta de nuevo.</p>}
          <label className="text-xs text-navy/50 block">Indicaciones para la IA (opcional)</label>
          <textarea data-testid="conceptboard-prompt" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Ej. estilo cálido, materiales naturales, enfoque lujoso pero acogedor…" className={input} />
          <button data-testid="conceptboard-generate" onClick={generate} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar Concept Board con IA
          </button>
        </div>
      ) : (
        <>
          {generating && (
            <div className="bg-violet/5 border border-violet/20 rounded-lg p-4 flex items-center gap-3" data-testid="conceptboard-generating">
              <Loader2 className="w-5 h-5 animate-spin text-violet" />
              <div className="text-sm text-navy/70">Generando dirección creativa e imágenes… {board.gen_total > 0 && `(${board.gen_done}/${board.gen_total} imágenes)`}</div>
            </div>
          )}
          {board.concept && (<div className="bg-white border border-navy/10 rounded-lg p-6"><div className="overline text-navy/40 mb-1">Dirección creativa</div><p className="text-navy/80 leading-relaxed">{board.concept}</p></div>)}
          {board.keywords?.length > 0 && (<div><div className="overline text-navy/40 mb-2">Mood / palabras clave</div><div className="flex flex-wrap gap-2">{board.keywords.map((k, i) => <span key={i} className="text-sm px-3 py-1 rounded-full bg-navy/5 text-navy/70">{k}</span>)}</div></div>)}
          {board.palette?.length > 0 && (<div><div className="overline text-navy/40 mb-2">Paleta de color</div><div className="flex flex-wrap gap-3">{board.palette.map((c, i) => (<div key={i} className="text-center"><div className="w-16 h-16 rounded-lg border border-navy/10" style={{ background: c.hex }} /><div className="text-[11px] mt-1 text-navy/60">{c.name}</div><div className="text-[10px] text-navy/35">{c.hex}</div></div>))}</div></div>)}
          <div className="grid sm:grid-cols-2 gap-4">
            {board.typography && (<div className="bg-white border border-navy/10 rounded-lg p-5"><div className="overline text-navy/40 mb-1">Tipografía</div><p className="text-sm text-navy/70">{board.typography}</p></div>)}
            {board.materials?.length > 0 && (<div className="bg-white border border-navy/10 rounded-lg p-5"><div className="overline text-navy/40 mb-2">{studio === "gout" ? "Materiales y acabados" : "Estilo visual de marca"}</div><ul className="text-sm text-navy/70 space-y-1">{board.materials.map((m, i) => <li key={i}>· {m}</li>)}</ul></div>)}
          </div>
        </>
      )}
    </div>
  );
}
